import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useBlocker } from 'react-router-dom';
import { VaModal } from '@department-of-veterans-affairs/component-library/dist/react-bindings';

// Asks before a route change out of QuickSubmit while `hasUnsavedWork` is true: Back,
// or any link out of the section. It asks BEFORE the URL moves, so the h1,
// title, and focus change only once the rep says yes. A full page load (a
// plain href, closing the tab) is not a route change, so this does not ask
// for it.
//
// A move inside `sectionPathname` for the same claimant is never blocked. The
// layout keeps the same embed for those, so no work is lost: upload and
// eForms, the confirmation (the packet is sent), or an eForm. The embed asks on its own
// before it closes an eForm with unsaved edits, and asking here too would ask
// twice. The claimant is in the query, and a new claimant gets a new embed, so
// a query change still asks.
const isInside = (pathname, sectionPathname) =>
  pathname === sectionPathname || pathname.startsWith(`${sectionPathname}/`);

const QuickSubmitLeaveGuard = ({ hasUnsavedWork, sectionPathname }) => {
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) => {
      if (!hasUnsavedWork) return false;
      if (currentLocation.search !== nextLocation.search) return true;
      if (isInside(nextLocation.pathname, sectionPathname)) return false;
      return currentLocation.pathname !== nextLocation.pathname;
    },
    [hasUnsavedWork, sectionPathname],
  );
  const blocker = useBlocker(shouldBlock);

  const leave = useCallback(() => blocker.proceed?.(), [blocker]);
  const stay = useCallback(() => blocker.reset?.(), [blocker]);

  return (
    <VaModal
      data-testid="qs-leave-modal"
      visible={blocker.state === 'blocked'}
      status="warning"
      modalTitle="Leave this page?"
      primaryButtonText="Yes, leave this page"
      secondaryButtonText="No, stay on this page"
      onPrimaryButtonClick={leave}
      onSecondaryButtonClick={stay}
      onCloseEvent={stay}
      uswds
    >
      <p>
        You have work on this page that isn’t saved. If you leave, you’ll lose
        it.
      </p>
    </VaModal>
  );
};

QuickSubmitLeaveGuard.propTypes = {
  hasUnsavedWork: PropTypes.bool.isRequired,
  sectionPathname: PropTypes.string.isRequired,
};

export default QuickSubmitLeaveGuard;
